import React from 'react'
import { SelectChangeEvent } from '@mui/material'
import { SxProps } from '@mui/material/styles'
import { UseFormReturn, useForm } from 'react-hook-form'
import { TextInput } from '../../../base'
import {
  COUNTRIES,
  CountryObject,
  getPostalCodeLabel,
  StateObject,
  countriesSx,
} from '../../../utils/countries'
import Dropdown, { ItemObject } from '../../../base/Dropdown/Dropdown'
import { inputProperties } from '../../../types/inputProperties'
import { validators } from '../../../utils/validators'
import InputGroup from '../../../base/InputGroup/InputGroup'
import PlacesAutocomplete, { Place } from './PlacesAutocomplete'

export interface AddressProps {
  autocomplete?: boolean
  city?: inputProperties
  country?: inputProperties
  form?: UseFormReturn
  line1?: inputProperties
  line2?: inputProperties
  postalCode?: inputProperties
  state?: inputProperties
  supportedCountriesAbbreviation?: Array<string>
  sx?: SxProps
}

const Address = (props: AddressProps) => {
  const useFormDefault = props.form || useForm()
  const { clearErrors, getValues, setValue } = useFormDefault

  const autocomplete =
    typeof props.autocomplete === 'boolean'
      ? props.autocomplete
      : Address.defaultProps.autocomplete

  const countryProps = Object.assign(
    Address.defaultProps.country,
    props.country,
  )
  const line1Props = Object.assign(Address.defaultProps.line1, props.line1)
  const line2Props = Object.assign(Address.defaultProps.line2, props.line2)
  const cityProps = Object.assign(Address.defaultProps.city, props.city)
  const stateProps = Object.assign(Address.defaultProps.state, props.state)
  const postalCodeProps = Object.assign(
    Address.defaultProps.postalCode,
    props.postalCode,
  )

  const getCountry = (country?: string) => {
    if (!country) {
      return null
    }
    if (country) {
      return COUNTRIES.find(({ abbreviation }) => abbreviation === country)
    }
  }

  const getStatesFromCountry = (country?: CountryObject) => {
    if (!country || !Array.isArray(country.states)) {
      return []
    }
    const statesFromCountry = country.states.map((item: StateObject) => ({
      value: item.abbreviation,
      label: item.abbreviation,
    }))
    return statesFromCountry || []
  }

  const defaultCountryCode = countryProps.value
  const countryCode = getValues(countryProps.name) || defaultCountryCode
  const countryData = getCountry(props.country?.value)

  const [stateLabel, setStateLabel] = React.useState(countryData?.stateTypeName)
  const [states, setStates] = React.useState(
    countryData ? getStatesFromCountry(countryData) : [],
  )
  const [postalCodeLabel, setPostalCodeLabel] = React.useState(
    getPostalCodeLabel(countryData?.abbreviation),
  )

  //Countries in format ItemObject
  const menuItems: Array<ItemObject> = COUNTRIES.filter(
    (item: CountryObject) =>
      !Array.isArray(props.supportedCountriesAbbreviation) ||
      props.supportedCountriesAbbreviation.includes(item.abbreviation),
  ).map((item: CountryObject) => ({
    value: item.abbreviation,
    label: item.name,
    className: `flag flagSpacing ${item.abbreviation}`,
  }))

  /**
   * `handleOnChangeCountry` gets the selected country and checks if it has states.
   * If so, the state field is changed from a state textInput to a state dropdown.
   */
  const handleOnChangeCountry = (event: SelectChangeEvent<string>) => {
    const countryData = getCountry(event.target.value)
    setPostalCodeLabel(getPostalCodeLabel(countryData?.abbreviation))
    setStates(countryData ? getStatesFromCountry(countryData) : [])
    if (countryData?.stateTypeName) {
      setStateLabel(countryData.stateTypeName)
    }
    setValue(stateProps.name, '')
    setValue(postalCodeProps.name, '')
    clearErrors(postalCodeProps.name)
  }

  const handleSelectAutocompletePlace = ({
    line1,
    city,
    state,
    postalCode,
    country,
  }: Place) => {
    clearErrors([
      line1Props.name,
      cityProps.name,
      stateProps.name,
      postalCodeProps.name,
      countryProps.name,
    ])
    setValue(line1Props.name, line1)
    setValue(cityProps.name, city)
    setValue(stateProps.name, state)
    setValue(postalCodeProps.name, postalCode)
    setValue(countryProps.name, country)
  }

  const addressLine1 = autocomplete ? (
    <PlacesAutocomplete
      countryCode={countryCode}
      defaultValue={line1Props?.value}
      form={useFormDefault}
      label={line1Props?.label}
      name={line1Props.name}
      onSelectPlaceCallback={handleSelectAutocompletePlace}
      required={line1Props?.required}
    />
  ) : (
    <TextInput
      form={useFormDefault}
      id={line1Props?.id}
      label={line1Props?.label}
      name={line1Props.name}
      required={line1Props?.required}
      rules={{ addressLine: validators.validateAddress }}
      value={line1Props?.value}
    />
  )

  return (
    <InputGroup
      inputRows={[
        [
          <Dropdown
            form={useFormDefault}
            required={countryProps?.required}
            onChange={handleOnChangeCountry}
            id={countryProps?.id}
            name={countryProps.name}
            label={countryProps?.label}
            items={menuItems}
            sx={countriesSx}
            menuProps={{ sx: { ...countriesSx } }}
            value={defaultCountryCode}
          />,
        ],
        [addressLine1],
        [
          <TextInput
            form={useFormDefault}
            id={line2Props?.id}
            label={line2Props?.label}
            name={line2Props.name}
            required={line2Props?.required}
            rules={{ addressLine: validators.validateAddress }}
            value={line2Props?.value}
          />,
        ],
        [
          <TextInput
            form={useFormDefault}
            id={cityProps?.id}
            label={cityProps?.label}
            name={cityProps.name}
            required={cityProps?.required}
            value={cityProps?.value}
            width={5.5}
          />,
          states.length ? (
            <Dropdown
              form={useFormDefault}
              id={stateProps?.id}
              items={states}
              label={stateLabel || stateProps?.label}
              name={stateProps.name}
              required={stateProps?.required}
              value={stateProps?.value}
              width={2.5}
            />
          ) : (
            <TextInput
              form={useFormDefault}
              id={stateProps?.id}
              label={stateLabel || stateProps?.label}
              name={stateProps.name}
              required={stateProps?.required}
              value={stateProps?.value}
              width={3.5}
            />
          ),
          <TextInput
            form={useFormDefault}
            id={postalCodeProps?.id}
            label={postalCodeLabel}
            name={postalCodeProps.name}
            required={postalCodeProps?.required}
            rules={{
              postalCode: validators.zipOrPostalCode(
                countryCode,
                Boolean(countryProps.required),
                Boolean(postalCodeProps.required),
              ),
            }}
            value={postalCodeProps?.value}
            width={4}
          />,
        ],
      ]}
    />
  )
}

Address.defaultProps = {
  autocomplete: true,
  country: {
    label: '',
    name: 'country',
    value: 'US',
    required: true,
  },
  line1: {
    label: 'Address',
    name: 'line1',
    required: true,
  },
  line2: {
    label: 'Apt, Bldg, Suite',
    name: 'line2',
  },
  city: {
    label: 'City',
    name: 'city',
    required: true,
  },
  state: {
    label: 'State',
    name: 'state',
    required: true,
  },
  postalCode: {
    name: 'postalCode',
    required: true,
  },
}

export default Address
